//------------------------------------------------------------------------------
// BFA10103G 共架申込 機器設備 - 地図
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { doShowMessage } from '@/reducks/common/operations';
import { StandardButton } from '@/components/atoms/Buttons/PositiveButton';
import backend from '@/common/backend';
import PoleItem from '@/utils/pole-item';
import SetubiMapBody from './map-body';
import SetubiMapNone from './map-none';
import ItemSelected from './item-selected';
import { PoleSelectForm, PoleSelectList } from './pole-list';

// 定数
const LOGH = 'BFA10103G map area:';

/**
 * 設備地図エリア
 */
export default function SetubiMapArea({
  mainPole,
  setubiList = [],
  setubiSelected = null,
  onSetubiAdd,
  onSetubiEdit,
  onSetubiDelete,
}) {

  // 状態
  const [ warnings, setWarnings ] = React.useState([]);   // メッセージ
  const [ poleList, setPoleList ] = React.useState([]);   // 電柱一覧

  // 選択電柱
  const [ itemSelected, setItemSelected ] = React.useState(null);

  // 申込電柱一覧 (重複のない電柱マップ)
  const orderPoles = React.useMemo(() => {
    const poles = new Map();

    // 電柱一覧
    setubiList?.forEach((stb) => {
      const pole = new PoleItem(stb);
      poles.set(pole.key(), pole);
    });

    // 電柱マップ
    return poles;
  }, [setubiList]);

  // メッセージ表示
  const dispatch = useDispatch();
  const showMessage = (props) => dispatch(doShowMessage(props));

  // 上位で設備選択 - 選択電柱更新
  React.useEffect(() => {

    // 選択解除なら何もしない
    if (!setubiSelected) return;

    // 選択した設備の電柱を状態保存
    const poleSelected = itemSelected?.pole;
    if (!poleSelected || !poleSelected.same(setubiSelected)) {
      const pole = new PoleItem(setubiSelected);
      setItemSelected(ItemSelected.pole(pole));
    }
  }, [setubiSelected]);

  //----------------------------------------------------------------------------
  // イベント
  //----------------------------------------------------------------------------
  // 電柱検索ボタン
  const handlePoleSearch = React.useCallback(async (formValues) => {
    console.info(LOGH, '電柱検索', formValues);

    // 電柱検索
    const res = await backend.post('/master/poles', formValues);
    const poleList = res
        .parse(showMessage)
        .parseBodyAsArray({
          max: 100,             // 最大100件
          error: 'BFY000015W',  // 上限超えメッセージ
        });

    // ステート更新
    updatePoleList(poleList, res.messages);
  }, []);

  // 申込電柱表示ボタン
  const handleOrderPoles = React.useCallback(() => {
    updatePoleList(Array.from(orderPoles.values()));
  }, [orderPoles]);

  // 電柱一覧更新
  const updatePoleList = React.useCallback((newPoleList, newWarnings) => {
    setItemSelected(null);
    setPoleList(newPoleList ?? []);
    setWarnings(newWarnings ?? []);
  }, []);

  // 電柱選択
  const handleItemSelect = React.useCallback((pole, opts) => {
    setItemSelected(ItemSelected.pole(pole, opts));
  }, []);

  //----------------------------------------------------------------------------
  // 描画
  //----------------------------------------------------------------------------
  return (
    <Stack direction="column">

      {/* [上段] 電柱検索パネル */}
      <Box
        sx={{
          px: 1, py: 1,
          borderTop: 1, borderColor: 'grey.400',  // 区切り線
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* 電柱検索フォーム */}
        <PoleSelectForm onSearch={handlePoleSearch} />

        {/* 設備一覧/径間一覧の電柱を表示 */}
        <Box sx={{ ml: 1 }}>
          <Tooltip arrow placement="top"
            title="申込登録中の電柱を表示します。"
          >
            <StandardButton
              variant="contained" size="medium"
              onClick={handleOrderPoles}
            >
              申込電柱表示
            </StandardButton>
          </Tooltip>
        </Box>
      </Box>

      {/*
        * [下段] コンテンツ本体
        */}
      <Stack
        direction="row" spacing={1}
        sx={{ pt: 1, height: '70vh' }}
      >
          {/* (左側) 電柱一覧 */}
          <Box>
            <PoleSelectList
              warnings={warnings}
              poleList={poleList}
              orderPoles={orderPoles}
              itemSelected={itemSelected}
              onItemSelect={handleItemSelect}
              onSetubiAdd={onSetubiAdd}
            />
          </Box>

          {/* (右側) 地図表示 */}
          <Box sx={{ flexGrow: 1 }}>
          {
            mainPole?.drawable()

              // 地図表示
              ? <SetubiMapBody
                  mainPole={mainPole}
                  poleList={poleList}
                  setubiList={setubiList}
                  orderPoles={orderPoles}
                  itemSelected={itemSelected}

                  // イベント
                  onPoleListSelect={updatePoleList}
                  onItemSelect={handleItemSelect}

                  // 設備一覧
                  onSetubiAdd={onSetubiAdd}
                  onSetubiEdit={onSetubiEdit}
                  onSetubiDelete={onSetubiDelete}
                />

              // 地図なし
              : <SetubiMapNone mainPole={mainPole} />
          }
          </Box>
      </Stack>
    </Stack>
  );
}

// プロパティ
SetubiMapArea.propTypes = {
  mainPole:       PropTypes.object,    // 代表電柱
  setubiList:     PropTypes.array,     // 設備リスト
  setubiSelected: PropTypes.object,    // 選択した設備

  // イベント処理
  onSetubiAdd:    PropTypes.func.isRequired,   // 設備追加
  onSetubiEdit:   PropTypes.func.isRequired,   // 設備編集
  onSetubiDelete: PropTypes.func.isRequired,   // 設備削除
};
